import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Edit, Trash2, Send, XCircle, CheckCircle2, Clock, AlertCircle,
  Loader2, ChevronDown, ChevronRight, ExternalLink
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useConfirm } from './ui';

/**
 * SubprocessorsManagement — v6.6.1 task #57
 *
 * Systemadmin-side for å administrere underdatabehandlere (sub_processors).
 * Bakgrunn: v6.4 Blokk 7 la til hele datamodell + backend + public-side, men
 * manglet admin-UI. Denne komponenten fyller det gapet.
 *
 * Handlinger:
 *   - Se full liste (inkl. draft/pending_notification/active/terminated)
 *   - Opprett ny (starter i draft)
 *   - Rediger draft-underleverandør
 *   - Publiser (starter 30-dagers varsel-flyt + e-post til signerte parter via #59)
 *   - Terminer (setter effective_until + varsler signerte parter)
 *   - Se endringslogg + ubehandlede motsigelser
 */

interface Subprocessor {
  id: string;
  slug: string;
  name: string;
  legal_entity_number: string | null;
  category: string;
  purpose: string;
  data_categories: string[];
  data_subjects: string[];
  legal_basis: string;
  location_country: string;
  transfer_mechanism: string | null;
  security_measures: string | null;
  applies_to_dpa: string[];
  status: 'draft' | 'pending_notification' | 'objection_period' | 'active' | 'terminated';
  privacy_policy_url: string | null;
  contact_email: string | null;
  contract_reference: string | null;
  effective_from: string | null;
  effective_until: string | null;
  display_order: number;
  notes: string | null;
  created_at: string;
}

interface Change {
  id: string;
  dpa_type: string;
  change_type: 'added' | 'removed' | 'modified';
  changes_summary: string;
  notification_sent_at: string | null;
  effective_from: string | null;
  objection_deadline: string | null;
  activated_at: string | null;
  notification_recipients_count: number | null;
  subprocessor_name: string | null;
  subprocessor_slug: string | null;
  objection_count: number;
}

interface Objection {
  id: string;
  subprocessor_change_id: string;
  party_type: string;
  organization_name: string;
  objection_text: string;
  submitted_at: string;
  resolution: string | null;
  subprocessor_name: string | null;
}

const CATEGORIES = [
  { value: 'hosting', label: 'Hosting og infrastruktur' },
  { value: 'email', label: 'E-post og kommunikasjon' },
  { value: 'identity', label: 'Innlogging og identitet' },
  { value: 'payment', label: 'Betaling' },
  { value: 'signing', label: 'Signering og forsegling' },
  { value: 'ai', label: 'Kunstig intelligens' },
  { value: 'lms', label: 'LMS-integrasjon' },
  { value: 'accounting', label: 'Regnskap og fakturering' },
  { value: 'sub_processor', label: 'Andre databehandlere' }
];

const STATUS_STYLES: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  draft: { label: 'Utkast', className: 'bg-gray-100 text-gray-700', icon: <Edit size={12} /> },
  pending_notification: { label: 'Varsling pågår', className: 'bg-amber-100 text-amber-800', icon: <Clock size={12} /> },
  objection_period: { label: 'Motsigelsesperiode', className: 'bg-amber-100 text-amber-800', icon: <Clock size={12} /> },
  active: { label: 'Aktiv', className: 'bg-green-100 text-green-800', icon: <CheckCircle2 size={12} /> },
  terminated: { label: 'Terminert', className: 'bg-red-100 text-red-800', icon: <XCircle size={12} /> }
};

export default function SubprocessorsManagement() {
  const { confirm } = useConfirm();
  const [tab, setTab] = useState<'list' | 'changes' | 'objections'>('list');
  const [items, setItems] = useState<Subprocessor[]>([]);
  const [changes, setChanges] = useState<Change[]>([]);
  const [objections, setObjections] = useState<Objection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Subprocessor | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rSub, rCh, rObj] = await Promise.all([
        authenticatedFetch('/api/admin/subprocessors'),
        authenticatedFetch('/api/admin/subprocessor-changes'),
        authenticatedFetch('/api/admin/subprocessor-objections')
      ]);
      if (!rSub.ok) throw new Error('Kunne ikke hente underleverandører');
      const d1 = await rSub.json();
      setItems(d1.subprocessors || []);
      if (rCh.ok) {
        const d2 = await rCh.json();
        setChanges(d2.changes || []);
      }
      if (rObj.ok) {
        const d3 = await rObj.json();
        setObjections(d3.objections || []);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async (id: string) => {
    const ok = await confirm({
      title: 'Publisere underleverandøren?',
      message: 'En publisering starter 30-dagers varsel-flyt og sender e-post til alle signerte parter.',
      variant: 'warning',
      confirmLabel: 'Publiser og send varsel',
      consequences: [
        '30-dagers varsel-periode starter umiddelbart',
        'Alle signerte parter mottar e-post',
        'Automatisk aktivering etter 30 dager hvis ingen motsigelser',
      ],
    });
    if (!ok) return;
    try {
      const r = await authenticatedFetch(`/api/admin/subprocessors/${id}/publish`, {
        method: 'POST'
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Publisering feilet');
      alert(`Varsel-flyt startet. Aktivering ${new Date(data.objection_deadline).toLocaleDateString('no-NO')} hvis ingen motsigelser.`);
      load();
    } catch (e) {
      alert(`Feil: ${(e as Error).message}`);
    }
  };

  const handleTerminate = async (id: string, name: string) => {
    const reason = prompt(`Terminere «${name}»? Skriv begrunnelse (min. 10 tegn):`);
    if (!reason || reason.length < 10) {
      if (reason !== null) alert('Begrunnelse må være minst 10 tegn.');
      return;
    }
    try {
      const r = await authenticatedFetch(`/api/admin/subprocessors/${id}/terminate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Terminering feilet');
      alert(`«${name}» er terminert. Signerte parter er varslet.`);
      load();
    } catch (e) {
      alert(`Feil: ${(e as Error).message}`);
    }
  };

  const filtered = items.filter(item => {
    if (filter === 'all') return true;
    return item.status === filter;
  });

  const draftCount = items.filter(i => i.status === 'draft').length;
  const pendingCount = items.filter(i => i.status === 'pending_notification' || i.status === 'objection_period').length;
  const openObjections = objections.filter(o => !o.resolution).length;

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-gray-500" size={32} /></div>;

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Underdatabehandlere</h1>
        <p className="text-sm text-gray-600 mt-1">
          Administrer leverandører i underdatabehandler-registeret. Publiserte endringer starter 30-dagers varsel-flyt
          til alle signerte parter, og sender e-postvarsler automatisk.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Tab-bar */}
      <div className="border-b border-gray-200 flex gap-4">
        {(['list', 'changes', 'objections'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`py-2 px-1 text-sm font-medium border-b-2 ${tab === t ? 'border-kf-blue-600 text-kf-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            {t === 'list' && `Leverandører (${items.length})`}
            {t === 'changes' && `Endringslogg (${changes.length})`}
            {t === 'objections' && (
              <>
                Motsigelser
                {openObjections > 0 && <span className="ml-1 px-2 py-0.5 bg-red-600 text-white rounded-full text-xs">{openObjections}</span>}
              </>
            )}
          </button>
        ))}
      </div>

      {tab === 'list' && (
        <>
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {['all', 'draft', 'pending_notification', 'active', 'terminated'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 text-xs rounded-full ${filter === f ? 'bg-kf-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {f === 'all' ? `Alle (${items.length})` : STATUS_STYLES[f]?.label || f}
                  {f === 'draft' && draftCount > 0 && ` (${draftCount})`}
                  {f === 'pending_notification' && pendingCount > 0 && ` (${pendingCount})`}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <a
                href="/underdatabehandlere"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-kf-blue-600 hover:underline inline-flex items-center gap-1"
              >
                Public side <ExternalLink size={12} />
              </a>
              <button
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded font-medium text-sm"
              >
                <Plus size={16} /> Ny underleverandør
              </button>
            </div>
          </div>

          <div className="bg-white shadow rounded overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Navn / kategori</th>
                  <th className="px-4 py-3 text-left">Lokasjon</th>
                  <th className="px-4 py-3 text-left">DPA-typer</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Handlinger</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map(item => (
                  <>
                    <tr key={item.id}>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                          className="text-left w-full flex items-start gap-2"
                        >
                          {expandedId === item.id ? <ChevronDown size={16} className="mt-1 text-gray-500" /> : <ChevronRight size={16} className="mt-1 text-gray-500" />}
                          <div>
                            <div className="font-medium text-gray-900">{item.name}</div>
                            <div className="text-xs text-gray-500">{CATEGORIES.find(c => c.value === item.category)?.label || item.category}</div>
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{item.location_country}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {(item.applies_to_dpa || []).map(d => (
                            <span key={d} className="text-xs px-2 py-0.5 bg-gray-100 rounded">{d}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[item.status]?.className || 'bg-gray-100'}`}>
                          {STATUS_STYLES[item.status]?.icon}
                          {STATUS_STYLES[item.status]?.label || item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setEditing(item)}
                            title="Rediger"
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                          >
                            <Edit size={14} />
                          </button>
                          {item.status === 'draft' && (
                            <button
                              onClick={() => handlePublish(item.id)}
                              title="Publiser (start 30-dagers varsel)"
                              className="p-1.5 hover:bg-kf-blue-100 rounded text-kf-blue-600"
                            >
                              <Send size={14} />
                            </button>
                          )}
                          {item.status === 'active' && (
                            <button
                              onClick={() => handleTerminate(item.id, item.name)}
                              title="Terminer"
                              className="p-1.5 hover:bg-red-100 rounded text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedId === item.id && (
                      <tr>
                        <td colSpan={5} className="px-4 py-4 bg-gray-50 border-b">
                          <div className="space-y-2 text-sm">
                            <div><strong>Formål:</strong> {item.purpose}</div>
                            {item.legal_entity_number && <div><strong>Org.nr.:</strong> {item.legal_entity_number}</div>}
                            {item.transfer_mechanism && <div><strong>Overføring:</strong> {item.transfer_mechanism}</div>}
                            {item.security_measures && <div><strong>Sikkerhetstiltak:</strong> {item.security_measures}</div>}
                            <div><strong>Legal basis:</strong> {item.legal_basis}</div>
                            {item.data_categories?.length > 0 && <div><strong>Datakategorier:</strong> {item.data_categories.join(', ')}</div>}
                            {item.privacy_policy_url && (
                              <div>
                                <strong>Personvernerklæring:</strong>{' '}
                                <a href={item.privacy_policy_url} target="_blank" rel="noopener noreferrer" className="text-kf-blue-600 hover:underline inline-flex items-center gap-1">
                                  {item.privacy_policy_url} <ExternalLink size={12} />
                                </a>
                              </div>
                            )}
                            {item.contact_email && <div><strong>Kontakt:</strong> {item.contact_email}</div>}
                            {item.notes && <div className="mt-2 p-2 bg-white rounded border text-xs text-gray-600 whitespace-pre-line">{item.notes}</div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'changes' && <ChangesTable changes={changes} />}
      {tab === 'objections' && <ObjectionsTable objections={objections} onChange={load} />}

      {(showCreate || editing) && (
        <EditModal
          initial={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ChangesTable({ changes }: { changes: Change[] }) {
  return (
    <div className="bg-white shadow rounded overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-3 text-left">Endring</th>
            <th className="px-4 py-3 text-left">DPA</th>
            <th className="px-4 py-3 text-left">Varsel sendt</th>
            <th className="px-4 py-3 text-left">Frist</th>
            <th className="px-4 py-3 text-left">Aktivert</th>
            <th className="px-4 py-3 text-left">Motsigelser</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {changes.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Ingen endringer registrert.</td></tr>
          )}
          {changes.map(c => (
            <tr key={c.id}>
              <td className="px-4 py-3">
                <div className="text-xs text-gray-500 uppercase">{c.change_type}</div>
                <div className="font-medium">{c.subprocessor_name || 'ukjent'}</div>
                <div className="text-xs text-gray-600 mt-1">{c.changes_summary}</div>
              </td>
              <td className="px-4 py-3 text-gray-700">{c.dpa_type}</td>
              <td className="px-4 py-3 text-gray-700 text-xs">
                {c.notification_sent_at ? new Date(c.notification_sent_at).toLocaleString('no-NO', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
              </td>
              <td className="px-4 py-3 text-gray-700 text-xs">
                {c.objection_deadline ? new Date(c.objection_deadline).toLocaleDateString('no-NO') : '—'}
              </td>
              <td className="px-4 py-3">
                {c.activated_at ? (
                  <span className="text-green-700 text-xs">✓ {new Date(c.activated_at).toLocaleDateString('no-NO')}</span>
                ) : (
                  <span className="text-gray-400 text-xs">Ikke aktivert</span>
                )}
              </td>
              <td className="px-4 py-3 text-center">
                {c.objection_count > 0 ? (
                  <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs">{c.objection_count}</span>
                ) : (
                  <span className="text-gray-400 text-xs">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ObjectionsTable({ objections, onChange }: { objections: Objection[]; onChange: () => void }) {
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const resolve = async (id: string, resolution: 'accepted' | 'rejected') => {
    const note = prompt(`Merknad ved ${resolution === 'accepted' ? 'aksept' : 'avvisning'} (valgfritt):`);
    if (note === null) return;  // avbrutt
    setResolvingId(id);
    try {
      const r = await authenticatedFetch(`/api/admin/subprocessor-objections/${id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution, notes: note || null })
      });
      if (!r.ok) throw new Error('Feilet');
      onChange();
    } catch (e) {
      alert(`Feil: ${(e as Error).message}`);
    } finally {
      setResolvingId(null);
    }
  };

  if (objections.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded p-6 text-center">
        <CheckCircle2 className="mx-auto text-green-600 mb-2" size={32} />
        <p className="text-green-800">Ingen ubehandlede motsigelser.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {objections.map(o => (
        <div key={o.id} className="bg-white shadow rounded p-4 border-l-4 border-amber-500">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <div className="text-xs text-gray-500 uppercase">{o.party_type}</div>
              <div className="font-medium">{o.organization_name}</div>
              <div className="text-xs text-gray-600 mt-1">
                Ang. underleverandør: <strong>{o.subprocessor_name || 'ukjent'}</strong>
              </div>
              <blockquote className="mt-2 p-3 bg-gray-50 border-l-2 border-gray-300 text-sm text-gray-800 whitespace-pre-line">
                {o.objection_text}
              </blockquote>
              <div className="text-xs text-gray-500 mt-2">
                Innsendt: {new Date(o.submitted_at).toLocaleString('no-NO', { dateStyle: 'long', timeStyle: 'short' })}
              </div>
            </div>
            {!o.resolution && (
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => resolve(o.id, 'accepted')}
                  disabled={resolvingId === o.id}
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs disabled:opacity-50"
                >
                  Aksepter
                </button>
                <button
                  onClick={() => resolve(o.id, 'rejected')}
                  disabled={resolvingId === o.id}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs disabled:opacity-50"
                >
                  Avvis
                </button>
              </div>
            )}
            {o.resolution && (
              <span className="text-xs px-2 py-1 bg-gray-100 rounded">{o.resolution}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function EditModal({ initial, onClose, onSaved }: { initial: Subprocessor | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    slug: initial?.slug || '',
    name: initial?.name || '',
    legal_entity_number: initial?.legal_entity_number || '',
    category: initial?.category || 'sub_processor',
    purpose: initial?.purpose || '',
    data_categories: initial?.data_categories?.join(', ') || '',
    location_country: initial?.location_country || 'Norge',
    transfer_mechanism: initial?.transfer_mechanism || '',
    security_measures: initial?.security_measures || '',
    applies_to_dpa: initial?.applies_to_dpa || ['dpa-2'],
    privacy_policy_url: initial?.privacy_policy_url || '',
    contact_email: initial?.contact_email || '',
    contract_reference: initial?.contract_reference || '',
    notes: initial?.notes || '',
    display_order: initial?.display_order?.toString() || '100'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!form.name || !form.purpose || !form.location_country) {
      setError('Navn, formål og lokasjon er påkrevd.');
      return;
    }
    if (!initial && !form.slug) {
      setError('Slug er påkrevd ved opprettelse (kort-ID, f.eks. "anthropic").');
      return;
    }
    setSaving(true);
    try {
      const url = initial ? `/api/admin/subprocessors/${initial.id}` : '/api/admin/subprocessors';
      const method = initial ? 'PATCH' : 'POST';
      const body: any = {
        name: form.name,
        legal_entity_number: form.legal_entity_number || null,
        category: form.category,
        purpose: form.purpose,
        data_categories: form.data_categories.split(',').map(s => s.trim()).filter(Boolean),
        location_country: form.location_country,
        transfer_mechanism: form.transfer_mechanism || null,
        security_measures: form.security_measures || null,
        applies_to_dpa: form.applies_to_dpa,
        privacy_policy_url: form.privacy_policy_url || null,
        contact_email: form.contact_email || null,
        contract_reference: form.contract_reference || null,
        notes: form.notes || null,
        display_order: parseInt(form.display_order, 10) || 100
      };
      if (!initial) body.slug = form.slug;

      const r = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Lagring feilet');
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold">{initial ? `Rediger: ${initial.name}` : 'Ny underleverandør'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>}

          {!initial && (
            <FormField label="Slug (kort-ID, permanent)" required>
              <input
                type="text"
                value={form.slug}
                onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="anthropic, tornado-data, tripletex"
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm font-mono"
              />
            </FormField>
          )}

          <FormField label="Navn" required>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Org.nr.">
              <input type="text" value={form.legal_entity_number} onChange={e => setForm({ ...form, legal_entity_number: e.target.value })}
                placeholder="Norsk org.nr. eller landsspesifikt" className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Kategori" required>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </FormField>
          </div>

          <FormField label="Formål" required>
            <textarea value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} rows={2}
              className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
          </FormField>

          <FormField label="Datakategorier (komma-separert)">
            <input type="text" value={form.data_categories} onChange={e => setForm({ ...form, data_categories: e.target.value })}
              placeholder="Navn, e-post, mobilnummer, fødselsdato"
              className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Lokasjon" required>
              <input type="text" value={form.location_country} onChange={e => setForm({ ...form, location_country: e.target.value })}
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Overføringsmekanisme">
              <input type="text" value={form.transfer_mechanism} onChange={e => setForm({ ...form, transfer_mechanism: e.target.value })}
                placeholder="EU/EØS, SCC, adekvansbeslutning" className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
            </FormField>
          </div>

          <FormField label="Sikkerhetstiltak">
            <textarea value={form.security_measures} onChange={e => setForm({ ...form, security_measures: e.target.value })} rows={2}
              className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
          </FormField>

          <FormField label="Gjelder DPA-typer (multi-select)">
            <div className="mt-1 flex flex-wrap gap-2">
              {['dpa-1', 'dpa-2', 'dpa-3'].map(t => (
                <label key={t} className="inline-flex items-center gap-1 px-3 py-1 border rounded cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={form.applies_to_dpa.includes(t)}
                    onChange={e => {
                      const newList = e.target.checked
                        ? [...form.applies_to_dpa, t]
                        : form.applies_to_dpa.filter(x => x !== t);
                      setForm({ ...form, applies_to_dpa: newList });
                    }}
                  />
                  {t}
                </label>
              ))}
            </div>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Personvernerklæring (URL)">
              <input type="url" value={form.privacy_policy_url} onChange={e => setForm({ ...form, privacy_policy_url: e.target.value })}
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Kontakt-e-post">
              <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Kontrakt-referanse (intern)">
              <input type="text" value={form.contract_reference} onChange={e => setForm({ ...form, contract_reference: e.target.value })}
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Sorterings-rekkefølge (lav = tidlig)">
              <input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })}
                className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
            </FormField>
          </div>

          <FormField label="Interne notater">
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm" />
          </FormField>
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded text-sm font-medium disabled:opacity-50">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {initial ? 'Lagre endringer' : 'Opprett'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      {children}
    </label>
  );
}
